import React from "react";
import { useAuth0, withAuthenticationRequired } from "@auth0/auth0-react";
import LoadingPage from "../LoadingPage";
import { ReactComponent as PayingClipArt } from "../../Assets/ZomentumGrowQuotePayPage/PayingClipArt.svg";
import { useTeamStore } from "../../Store/Team";
import { useCustomerStore } from "../../Store/Customer";
import { useParams } from "react-router-dom";
import ZomentumLogo from "../../Assets/PaymentsAppHeader/ZomentumLogo.svg";
import { AdyenPaymentMethodType } from "../../Contracts/Transaction";
import { AdyenMandatedBy } from "../../Contracts/AdyenSession";
import { usePaymentMethodManagementStore } from "../../Store/PaymentMethodManagement";
import Lottie from "react-lottie";
import CheckMarkLottie from "../../Assets/ClientPortalLoginPage/CheckMarkLottie.json";
import PaymentForm from "../../Components/Organisms/PaymentForm";
import MessageBanner from "../../Components/Molecules/PaymentsAppHeader/MessageBanner";

const AddPaymentMethodMandatePage: React.FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const { getCurrentTeam, currentTeam, formatCurrency } = useTeamStore();
  const { getCustomerDetails, customerDetailEntity } = useCustomerStore();
  const {
    createAddPaymentMethodMandate,
    doGiactValidation,
    sendMandateCreationNotification,
    handleBacsPayment,
    createTransactionToken
  } = usePaymentMethodManagementStore().partnerPortalFunctions;
  const { paymentsAdyenSessionData } = usePaymentMethodManagementStore();
  const { id: customerID } = useParams();

  const [behalfValidationError, setBehalfValidationError] = React.useState<boolean>(false);
  const [consent, setConsent] = React.useState<boolean>(false);
  const [paymentMethodAddedSuccess, setPaymentMethodAddedSuccess] = React.useState<boolean>(false);

  const initialLoad = React.useCallback(async () => {
    customerID &&
      (await Promise.all([
        getCurrentTeam(getAccessTokenSilently),
        getCustomerDetails(getAccessTokenSilently, customerID)
      ]));
  }, [getCustomerDetails, getCurrentTeam, getAccessTokenSilently, customerID]);

  React.useEffect(() => {
    initialLoad();
  }, [initialLoad]);

  const paymentMethodTypesToShow =
    customerDetailEntity?.allowed_payment_methods?.data.filter(m => m.enabled).map(m => m.type) ||
    [];

  return paymentMethodAddedSuccess ? (
    <div className="flex flex-col items-center h-screen bg-gray-100 p-36">
      <div className="flex items-center absolute left-0 top-0 m-4 space-x-2">
        {currentTeam?.partner_white_label_metadata?.branding_company_logo_s3_url &&
          currentTeam?.partner_white_label_metadata.branding_company_logo_s3_url.length > 0 && (
            <img
              src={currentTeam?.partner_white_label_metadata.branding_company_logo_s3_url}
              alt="Company Logo"
              className="max-h-10"
            />
          )}
        <span className="font-semibold">{currentTeam?.partner_white_label_metadata?.name}</span>
      </div>
      <div className="flex flex-col items-center space-y-4 bg-white px-36 py-12">
        <Lottie
          options={{ animationData: CheckMarkLottie }}
          width="14rem"
          isClickToPauseDisabled
          style={{ cursor: "default" }}
        />
        <div className="text-lime-600 text-2xl font-semibold">Request Successful!</div>
        <div className="text-center">
          The authorization request for your payment method was successful
        </div>
      </div>
    </div>
  ) : (
    <>
      <MessageBanner />
      <div className="grid grid-cols-5 h-screen">
        <div className="md:col-span-3 col-span-5 bg-white-smoke">
          <div className="flex items-center space-x-2 m-8">
            {currentTeam?.partner_white_label_metadata.branding_company_logo_s3_url && (
              <img
                src={currentTeam?.partner_white_label_metadata.branding_company_logo_s3_url}
                alt="Company logo"
                className="max-h-10"
              />
            )}
            <div className="font-semibold">{currentTeam?.partner_white_label_metadata.name}</div>
          </div>
          <div className="w-full flex justify-center md:my-48 my-20">
            <PayingClipArt />
          </div>
        </div>
        <div className="md:col-span-2 col-span-5 p-8 overflow-y-auto">
          <div className="border-b pb-2">
            <div className="text-2xl font-bold text-gray-700 mb-2">Add Authorization/Mandate</div>
            <div className="my-3 font-semibold">Review Payment</div>
            <div className="flex justify-between bg-[rgba(240,245,255,0.5)] p-4 my-4 rounded font-semibold">
              <div>Amount due</div>
              <div>{formatCurrency(0)}</div>
            </div>
            <div
              className={`p-3 flex space-x-2 items-center cursor-pointer ${
                behalfValidationError && !consent && "border border-red-400 rounded p-2"
              }`}
              onClick={() => {
                setConsent(!consent);
                consent && setBehalfValidationError(false);
              }}
            >
              <input
                type="checkbox"
                id="consent"
                checked={consent}
                onChange={() => setConsent(!consent)}
              />
              <div>I am authorized by the client to add payment details on their behalf.</div>
            </div>
            {behalfValidationError && !consent && (
              <div className="text-red-600">Your consent is required!</div>
            )}
          </div>

          <PaymentForm
            allowedPaymentMethods={paymentMethodTypesToShow}
            fetchAdyenSessionForMethod={async method => {
              await createAddPaymentMethodMandate(getAccessTokenSilently, {
                customer_id: parseInt(customerID as string),
                method,
                mandated_by: AdyenMandatedBy.Self
              });
            }}
            sessionData={paymentsAdyenSessionData?.session}
            callNotifyRoute={async result => {
              if (!paymentsAdyenSessionData) {
                console.error("No session data available");
                return Promise.reject("Unexpected error occured, there is no paymentsSessionData");
              }
              return await sendMandateCreationNotification(
                getAccessTokenSilently,
                result,
                paymentsAdyenSessionData.transaction_data.reference
              );
            }}
            callTransactionRoute={async (method: AdyenPaymentMethodType) => {
              if (!paymentsAdyenSessionData || !customerID) {
                console.error("No session data available");
                return Promise.reject(
                  "Unexpected error occured, there is no paymentsSessionData or customerId"
                );
              }
              return await createTransactionToken(
                getAccessTokenSilently,
                paymentsAdyenSessionData.transaction_data.reference,
                parseInt(customerID),
                method,
                paymentsAdyenSessionData?.transaction_data.expires_at
              );
            }}
            doGiactValidation={async (billingAddress: any, method: AdyenPaymentMethodType) => {
              if (!customerID) {
                console.error("No customer ID available");
                return Promise.reject("Unexpected error occured, there is no customerId");
              }
              return await doGiactValidation(getAccessTokenSilently, {
                customer_id: parseInt(customerID),
                billing_address_data: billingAddress,
                payment_method: method,
                referrer: window.location.href
              });
            }}
            callBacsPaymentRoute={async (data, transactionReference) => {
              return (await handleBacsPayment(getAccessTokenSilently, transactionReference, data))
                .success;
            }}
            onPaymentCompleted={() => setPaymentMethodAddedSuccess(true)}
            translations={{
              confirmPurchase: "Setup Direct Debit Mandate",
              "bacs.confirm": "Setup Direct Debit Mandate"
            }}
            beforeSubmitValidation={async method => {
              const consentFromElement = document.getElementById("consent") as HTMLInputElement;
              if (!consentFromElement.checked) {
                setBehalfValidationError(true);
                return false;
              }
              setBehalfValidationError(false);
              return true;
            }}
          />

          <div className="text-gray-500 px-8 my-4 text-center">
            Your payment information will be stored securely by our payment processor for later use.
            You will not be charged now and will be notified before any future charges.
          </div>

          <div className="flex items-center justify-center space-x-2 m-4 opacity-60">
            <span className="text-xs mt-0.5">Powered by </span>
            <img src={ZomentumLogo} alt="Zomentum Logo" className="w-20" />
          </div>
        </div>
      </div>
    </>
  );
};

export default withAuthenticationRequired(AddPaymentMethodMandatePage, {
  onRedirecting: () => (
    <div className="w-full h-screen flex items-center justify-center">
      <LoadingPage />
    </div>
  )
});
